<template>
  <div>
    <SiteConclusion @successfully="successfully" :items="conclusionSite" v-if="successfulPassword"/>
    <template v-else>
      <Breadcrumbs :items="itemsBreadcrumbs" class="ml-n6" />
      <Heading :title="t$.routeTitle" :subtitle="headerSubTitle" header headerColor="gray scale200"/>
      <LoadingArea :display-content="displayContent">
        <template #loading>
          <Row>
            <Col cols="12" md="6">
              <SkeletonLoader v-for="i in 5" :key="i" class="mx-auto mt-4" type="list-item" elevation="1"/>
            </Col>
          </Row>
        </template>
        <template>
          <Row>
            <Col cols="12" md="6">
              <Row>
                <Col cols="12">
                  <SelectField
                   class="pb-4"
                   :label="t$.user.label"
                   :placeholder="t$.user.placeholder"
                   :items="users"
                   v-model="userWp"
                   required
                   tooltipPosition="right"
                  />
                </Col>

                <Col cols="12">
                  <PasswordField required :label="t$.password.label" :placeholder="t$.password.placeholder" maxLength="30" minLength="8" v-model="passwordToConfirm" validation @blur="$v.passwordToConfirm.$touch()" :errorMessages="passwordToConfirmErrors"/>
                </Col>

                <Col cols="12">
                  <PasswordField required :label="t$.confirmPassword.label" :placeholder="t$.confirmPassword.placeholder" maxLength="30" minLength="8" v-model="passwordConfirmation" :errorMessages="passwordConfirmationErrors" @blur="$v.passwordConfirmation.$touch()"/>
                </Col>

                <Col cols="12">
                  <form @submit.prevent="handleUpdatePassword">
                    <Button v-bind="buttonProps" :label="t$.button" color="primary" :loading="loadingButton" type="submit" block/>
                  </form>
                </Col>
              </Row>
            </Col>
          </Row>
        </template>
      </LoadingArea>
    </template>
  </div>
</template>

<script>
import {
  Row,
  Col,
  Heading,
  Button,
  SelectField,
  PasswordField,
  useLocale,
  passwordErrorMessages,
  passwordValidation,
  confirmPasswordErrorMessages,
  confirmPasswordValidation,
  Breadcrumbs,
  LoadingArea,
  SkeletonLoader
} from '@locaweb/design-system-vue'
import { changePassword, findUsers, findIdWp } from '@/views/site/api/site.api'
import SiteConclusion from '@/views/site/list/components/SiteConclusion'
import { validationMixin } from 'vuelidate'

export default {
  name: 'SitePassword',
  components: {
    Row,
    Col,
    Heading,
    Button,
    SelectField,
    PasswordField,
    Breadcrumbs,
    LoadingArea,
    SkeletonLoader,
    SiteConclusion
  },
  mixins: [validationMixin],
  data: () => ({
    users: {},
    idWp: '',
    passwordToConfirm: '',
    passwordConfirmation: '',
    userWp: '',
    displayContent: false,
    successfulPassword: false,
    loadingButton: false
  }),
  setup () {
    const t$ = useLocale('sites.password')

    return {
      t$
    }
  },
  methods: {
    async findId () {
      try {
        const domain = this.dataSite.domain
        const id = await findIdWp(domain)
        this.idWp = id.data.id
        this.usersFind(this.idWp)
      } catch (error) {
        console.error(error)
      }
    },
    async usersFind (id) {
      try {
        const users = await findUsers(id)
        this.users = users.data.users
        this.userWp = users.data.users[0]
        if (users.status === 200) {
          this.displayContent = true
        }
      } catch (error) {
        console.error(error)
      }
    },
    async handleUpdatePassword () {
      this.loadingButton = true
      const site = {
        password: this.passwordConfirmation,
        user: this.userWp
      }
      if (this.passwordToConfirmErrors.length <= 0 && this.passwordConfirmationErrors.length <= 0) {
        try {
          const receiveChange = await changePassword(site, this.idWp)
          if (receiveChange.status === 200) {
            this.loadingButton = false
            this.successfulPassword = true
          }
        } catch (e) {
          this.loadingButton = false
          this.sendNotification()
          throw e
        }
      }
    },
    sendNotification (dispensable = true) {
      this.$notify.show({
        icon: '$symbols-alert-filled',
        message: 'Não foi possível alterar sua senha. Tente novamente mais tarde.',
        state: 'warning',
        dispensable,
        timeout: 7000
      })
    },
    successfully () {
      this.$router.push(`/${this.dataSite.plan_id}`)
    }
  },
  validations: {
    password: passwordValidation({ minLength: 8, maxLength: 20 }),
    passwordToConfirm: passwordValidation({ minLength: 2, maxLength: 20 }),
    passwordConfirmation: confirmPasswordValidation('passwordToConfirm')
  },
  computed: {
    conclusionSite () {
      const href = `https://${this.dataSite.domain}`
      const data = this.t$.conclusion
      data.actions[1].href = data.actions[1].href.replace('{ domain }', href)
      return data
    },
    submitStatus () {
      let error = ''
      if (this.passwordToConfirmErrors > 0) {
        error = true
      }
      return error
    },
    dataSite () {
      const sites = JSON.parse(localStorage.getItem('dataSites'))
      const data = sites.filter(item => parseInt(this.$attrs.id) === item.id)
      return data[0]
    },
    headerSubTitle () {
      const subTitle = this.t$.subtitle.replaceValues(this.dataSite)
      return subTitle
    },
    itemsBreadcrumbs () {
      const title = JSON.parse(localStorage.getItem('headerProps'))
      const sites = JSON.parse(localStorage.getItem('dataSites'))
      const data = sites.filter(item => parseInt(this.$attrs.id) === item.id)

      const items = [
        { text: 'Listagem de Planos', to: '/', disabled: false, exact: true },
        { text: title, to: `/${data[0].plan_id}`, disabled: false, exact: true },
        { text: 'Alterar Senha', to: '/', disabled: true, exact: true }
      ]
      return items
    },
    buttonProps () {
      let status = true
      if (
        this.passwordConfirmation &&
        this.passwordConfirmation === this.passwordToConfirm
      ) {
        if (
          this.passwordToConfirmErrors.length <= 0 &&
          this.passwordConfirmationErrors.length <= 0
        ) {
          status = false
        } else {
          status = true
        }
      }
      const props = {
        true: { disabled: true },
        false: { disabled: false }
      }
      return props[status]
    },
    ...passwordErrorMessages('password'),
    ...passwordErrorMessages('passwordToConfirm'),
    ...confirmPasswordErrorMessages('passwordConfirmation')
  },
  mounted () {
    this.findId()
  }
}
</script>
